import React from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation } from '@tanstack/react-query';
import { CreditCard, ShieldCheck, Loader2, Rocket } from 'lucide-react';
import { createOpleiderStripeOnboardingLink } from '../../services/opleidingenApi';
import { foutMelding, OpleiderOnboardingState } from '../../services/opleiderOnboardingApi';
import { Kaart, StapKop, Knop, Melding } from './ui';

export const StapStripe: React.FC<{ state: OpleiderOnboardingState; terugUitStripe: boolean }> = ({ state, terugUitStripe }) => {
  const { t } = useTranslation('pages');
  const koppel = useMutation({
    mutationFn: createOpleiderStripeOnboardingLink,
    // Dieplink in hetzelfde tabblad: Stripe stuurt terug naar /opleider/onboarding?stripe=return.
    onSuccess: ({ onboarding_url }) => {
      if (onboarding_url) window.location.assign(onboarding_url);
    },
  });

  const wachtOpStripe = terugUitStripe && state.stripe_status === 'pending';

  return (
    <Kaart className="text-center">
      <div className="text-left">
        <StapKop
          nummer={5}
          titel={t('opleiderWizard.stripe.titel', { defaultValue: 'Stripe Connect activatie' })}
          intro={t('opleiderWizard.stripe.intro', {
            defaultValue:
              'De laatste stap richt de geautomatiseerde uitbetaling in. In de beveiligde Stripe-omgeving vult u uw IBAN, bedrijfsgegevens en identificatie in; BeLagom ziet uw bankgegevens nooit.',
          })}
        />
      </div>

      {wachtOpStripe ? (
        <div className="py-8" role="status">
          <Loader2 className="mx-auto h-10 w-10 animate-spin text-sky-400" aria-hidden="true" />
          <p className="mt-4 font-semibold text-white">{t('opleiderWizard.stripe.controle', { defaultValue: 'Stripe controleert uw gegevens…' })}</p>
          <p className="mt-1 text-sm text-slate-400">
            {t('opleiderWizard.stripe.controleUitleg', {
              defaultValue: 'Zodra Stripe uw account goedkeurt, opent uw cockpit automatisch. Dit duurt meestal enkele minuten; u kunt dit venster sluiten en later terugkomen.',
            })}
          </p>
        </div>
      ) : (
        <div className="py-6">
          {state.stripe_status === 'rejected' && (
            <div className="mb-5 text-left">
              <Melding>
                {t('opleiderWizard.stripe.afgewezen', {
                  defaultValue: 'Stripe heeft aanvullende informatie nodig of kon uw gegevens niet verifiëren. Open de koppeling opnieuw om dit af te ronden.',
                })}
              </Melding>
            </div>
          )}
          {state.stripe_status === 'pending' && !terugUitStripe && (
            <div className="mb-5 text-left">
              <Melding soort="info">
                {t('opleiderWizard.stripe.onvolledig', { defaultValue: 'Uw Stripe-koppeling is gestart maar nog niet afgerond.' })}
              </Melding>
            </div>
          )}
          <Knop groot className="w-full sm:w-auto" bezig={koppel.isPending} onClick={() => koppel.mutate()}>
            {!koppel.isPending && <CreditCard className="h-5 w-5" aria-hidden="true" />}
            {t('opleiderWizard.stripe.knop', { defaultValue: 'Koppel Bankrekening via Stripe Connect' })}
          </Knop>
          {koppel.isError && (
            <div className="mt-5 text-left">
              <Melding>{foutMelding(koppel.error, t('opleiderWizard.stripe.fout', { defaultValue: 'De Stripe-koppeling kon niet worden gestart.' }))}</Melding>
            </div>
          )}
          <p className="mt-5 inline-flex items-center gap-1.5 text-xs text-slate-500">
            <ShieldCheck className="h-3.5 w-3.5" aria-hidden="true" />
            {t('opleiderWizard.stripe.veilig', { defaultValue: 'Beveiligde verificatie door Stripe (PSD2 / KYC)' })}
          </p>
        </div>
      )}
    </Kaart>
  );
};

/** Het go-live-moment: feestelijke overlay met de definitieve startknop. */
export const GoLive: React.FC<{ onStart: () => void }> = ({ onStart }) => {
  const { t } = useTranslation('pages');
  const kleuren = ['#38bdf8', '#34d399', '#fbbf24', '#f472b6', '#a78bfa'];
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center overflow-hidden bg-[#0B0F17]/95 px-4" role="dialog" aria-modal="true" aria-labelledby="golive-titel">
      <style>{`
        @keyframes blg-confetti { 0% { transform: translate3d(0,-10vh,0) rotate(0deg); opacity: 1 } 100% { transform: translate3d(var(--dx),110vh,0) rotate(720deg); opacity: .9 } }
        @keyframes blg-pop { 0% { transform: scale(.85); opacity: 0 } 100% { transform: scale(1); opacity: 1 } }
        @media (prefers-reduced-motion: reduce) { .blg-confetti { display: none } .blg-pop { animation: none !important } }
      `}</style>
      {Array.from({ length: 70 }).map((_, i) => (
        <span
          key={i}
          aria-hidden="true"
          className="blg-confetti pointer-events-none absolute top-0 block h-3 w-1.5 rounded-sm"
          style={{
            left: `${(i * 37) % 100}%`,
            background: kleuren[i % kleuren.length],
            animation: `blg-confetti ${2.6 + (i % 7) * 0.35}s ${(i % 10) * 0.12}s cubic-bezier(.2,.6,.4,1) forwards`,
            ['--dx' as string]: `${((i * 53) % 200) - 100}px`,
          }}
        />
      ))}
      <div className="blg-pop relative max-w-lg text-center" style={{ animation: 'blg-pop .5s ease-out both' }}>
        <div className="mx-auto mb-6 flex h-20 w-20 items-center justify-center rounded-3xl bg-emerald-400/15 ring-1 ring-emerald-400/40">
          <Rocket className="h-10 w-10 text-emerald-300" aria-hidden="true" />
        </div>
        <h2 id="golive-titel" className="text-3xl font-extrabold text-white">
          {t('opleiderWizard.golive.titel', { defaultValue: 'U bent live op BeLagom!' })}
        </h2>
        <p className="mt-3 text-slate-300">
          {t('opleiderWizard.golive.tekst', {
            defaultValue: 'Uw account is financieel gevalideerd en alle menu’s zijn ontgrendeld. Tijd om uw eerste cursus aan de Academy toe te voegen.',
          })}
        </p>
        <Knop groot className="mt-8" onClick={onStart} autoFocus>
          🚀 {t('opleiderWizard.golive.knop', { defaultValue: 'Open Mijn Dashboard & Voeg Eerste Cursus Toe' })}
        </Knop>
      </div>
    </div>
  );
};
